/**
 * Einen Schritt an jemand anderen übergeben (Richtlinie 21).
 *
 * Die Matrix sagt, bei welcher Rolle ein Schritt liegt – und damit bei wem.
 * Wer davon abweicht, weil jemand im Urlaub ist oder es so abgesprochen
 * wurde, tut das hier und sagt, warum. Die Übergabe gilt nur für diesen
 * Schritt; mit dem nächsten greift wieder die Matrix. Der Grund steht in der
 * Chronik des Vorgangs.
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../api';
import { useKora, useMeta } from '../zustand';
import { Modal, KnopfPrimaer, KnopfSekundaer, Pflicht, PflichtLegende } from '../komponenten/Modal';
import type { Angebot, LiegtBei } from '../typen';

export function ZustaendigModal({
  angebot,
  bestellungId = null,
  liegtBei,
  onSchliessen,
  onFertig,
}: {
  angebot: Angebot;
  /** Gesetzt, wenn eine Bestellung übergeben wird, nicht das Angebot selbst. */
  bestellungId?: number | null;
  liegtBei: LiegtBei;
  onSchliessen: () => void;
  onFertig: (a: Angebot) => void;
}) {
  const meta = useMeta();
  const { toastZeigen } = useKora();
  const [person, setPerson] = useState('');
  const [grund, setGrund] = useState('');
  const [fehler, setFehler] = useState('');

  const kandidaten = meta.team.filter((t) => t.name !== liegtBei.person);

  const uebergeben = useMutation({
    mutationFn: () =>
      api.sende<Angebot>('/zustaendigkeit/uebergeben', {
        angebotId: angebot.id,
        bestellungId,
        person,
        grund: grund.trim(),
      }),
    onSuccess: (a) => {
      toastZeigen(`Liegt jetzt bei ${meta.team.find((t) => t.name === person)?.anzeigename ?? person}.`);
      onFertig(a);
    },
    onError: (f) => setFehler(f instanceof Error ? f.message : 'Das hat nicht geklappt.'),
  });

  const zurueck = useMutation({
    mutationFn: () =>
      api.sende<Angebot>('/zustaendigkeit/zuruecknehmen', { angebotId: angebot.id, bestellungId }),
    onSuccess: (a) => {
      toastZeigen('Liegt wieder bei der Rolle laut Zuständigkeiten.');
      onFertig(a);
    },
    onError: (f) => setFehler(f instanceof Error ? f.message : 'Das hat nicht geklappt.'),
  });

  function abschicken() {
    if (!person) {
      setFehler('Bitte eine Person wählen.');
      return;
    }
    if (grund.trim().length < 3) {
      setFehler('Bitte begründen, warum der Vorgang wechselt.');
      return;
    }
    setFehler('');
    uebergeben.mutate();
  }

  const laeuft = uebergeben.isPending || zurueck.isPending;

  return (
    <Modal
      titel="Vorgang übergeben"
      untertitel={`${angebot.name} · ${liegtBei.schrittLabel}`}
      breite={520}
      onSchliessen={onSchliessen}
      fuss={
        <>
          {liegtBei.uebergeben && (
            <KnopfSekundaer onClick={() => zurueck.mutate()} disabled={laeuft}>
              Zurück an {liegtBei.rolleLabel}
            </KnopfSekundaer>
          )}
          <KnopfSekundaer onClick={onSchliessen} disabled={laeuft}>
            Abbrechen
          </KnopfSekundaer>
          <KnopfPrimaer onClick={abschicken} disabled={laeuft}>
            {uebergeben.isPending ? 'Wird übergeben …' : 'Übergeben'}
          </KnopfPrimaer>
        </>
      }
    >
      <div className="modal-hinweis">
        Laut Zuständigkeiten liegt dieser Schritt beim <strong>{liegtBei.rolleLabel}</strong>
        {liegtBei.uebergeben ? (
          <>
            {' '}– übergeben von {liegtBei.uebergeben.von} am {liegtBei.uebergeben.amText} an{' '}
            <strong>{liegtBei.personName}</strong>: „{liegtBei.uebergeben.grund}"
          </>
        ) : (
          <>
            , also bei <strong>{liegtBei.personName}</strong>
          </>
        )}
        . Die Übergabe gilt für genau diesen Schritt; mit dem nächsten greift wieder die Matrix.
      </div>

      <label className="modal-feld">
        <span>
          An wen
          <Pflicht />
        </span>
        <select
          className="modal-auswahl"
          value={person}
          onChange={(e) => {
            setPerson(e.target.value);
            setFehler('');
          }}
          disabled={laeuft}
        >
          <option value="">Person wählen …</option>
          {kandidaten.map((t) => (
            <option key={t.name} value={t.name}>
              {t.anzeigename}
              {t.rolle ? ` · ${t.rolle}` : ''}
            </option>
          ))}
        </select>
      </label>

      <label className="modal-feld">
        <span>
          Warum
          <Pflicht />
        </span>
        <textarea
          className="modal-textarea"
          rows={3}
          value={grund}
          placeholder="z. B. Urlaub bis 28.09., Kunde ist bei Tibor bekannt, Absprache im Team …"
          onChange={(e) => {
            setGrund(e.target.value);
            setFehler('');
          }}
          disabled={laeuft}
        />
      </label>

      {fehler && <div className="eh-fehler">{fehler}</div>}
      <PflichtLegende />
    </Modal>
  );
}
